import { mergeRunTimeline, type TimelineSource } from './run-timeline';
import { useRunLogs } from './use-run-logs';
import {
  applySuggestion,
  getAutocomplete,
  type LogAutocompleteContext,
} from '@/components/v1/cloud/logging/log-search/autocomplete';
import { parseLogQuery } from '@/components/v1/cloud/logging/log-search/parser';
import type { AutocompleteSuggestion } from '@/components/v1/cloud/logging/log-search/types';
import type { LogLine } from '@/components/v1/cloud/logging/log-search/use-logs';
import type { TimelineTime } from '@/components/v1/cloud/logging/log-timeline';
import { LogViewer } from '@/components/v1/cloud/logging/log-viewer';
import { SearchBarWithFilters } from '@/components/v1/molecules/search-bar-with-filters/search-bar-with-filters';
import { Button } from '@/components/v1/ui/button';
import { useMemo, useState } from 'react';

const EMPTY_QUERY = parseLogQuery('');
const AUTOCOMPLETE_CONTEXT: LogAutocompleteContext = {};

interface RunActivityTimelineProps {
  events: LogLine[];
  taskExternalIds: string[];
  taskStartTimes: ReadonlyMap<string, TimelineTime>;
  showTaskName: boolean;
  isLoadingEvents: boolean;
  eventError?: string;
  onViewRun: (taskExternalId: string) => void;
}

export function RunActivityTimeline({
  events,
  taskExternalIds,
  taskStartTimes,
  showTaskName,
  isLoadingEvents,
  eventError,
  onViewRun,
}: RunActivityTimelineProps) {
  const [queryString, setQueryString] = useState('');
  const [source, setSource] = useState<TimelineSource>('all');
  const [isAtTop, setAtTop] = useState(true);
  const query = useMemo(() => parseLogQuery(queryString), [queryString]);
  // Fetch an unfiltered window so search, level and attempt mean the same thing
  // for events and application logs, including when the search has no matches.
  const { logs, logsQuery, fetchOlderLogs } = useRunLogs(
    taskExternalIds,
    EMPTY_QUERY,
    isAtTop,
  );
  const hasOlderLogs = !!logsQuery.hasNextPage;
  const rows = useMemo(
    () => mergeRunTimeline(events, logs, hasOlderLogs, source, query),
    [events, logs, hasOlderLogs, source, query],
  );
  const isLoading =
    rows.length === 0 &&
    ((source !== 'logs' && isLoadingEvents) ||
      (source !== 'events' && logsQuery.isLoading));

  const updateQuery = (value: string) => {
    setQueryString(value);
    setAtTop(true);
  };

  return (
    <div
      className="flex min-h-48 flex-1 flex-col gap-2"
      data-testid="run-activity-timeline"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1 basis-64">
          <SearchBarWithFilters<AutocompleteSuggestion, LogAutocompleteContext>
            value={queryString}
            onChange={updateQuery}
            onSubmit={updateQuery}
            getAutocomplete={getAutocomplete}
            applySuggestion={applySuggestion}
            autocompleteContext={AUTOCOMPLETE_CONTEXT}
            placeholder="Search events and logs..."
            filterChips={[
              {
                key: 'level:',
                label: 'Level',
                description: 'Filter events and logs by level',
              },
              {
                key: 'attempt:',
                label: 'Attempt',
                description: 'Filter by attempt number',
              },
            ]}
          />
        </div>
        <select
          aria-label="Activity source"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          value={source}
          onChange={(event) => {
            setSource(event.target.value as TimelineSource);
            setAtTop(true);
          }}
        >
          <option value="all">All sources</option>
          <option value="events">Events</option>
          <option value="logs">Logs</option>
        </select>
      </div>
      {!query.isValid && (
        <p role="status" className="text-sm text-muted-foreground">
          {query.errors.join(' ')}
        </p>
      )}
      {source !== 'events' && logsQuery.isLoading && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading application logs...
        </p>
      )}
      {source !== 'logs' && isLoadingEvents && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading run events...
        </p>
      )}
      {eventError && (
        <p role="alert" className="text-sm text-destructive">
          {eventError}
        </p>
      )}
      {logsQuery.isError && (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm text-destructive"
        >
          <span>
            Application logs could not be loaded. Activity may be incomplete.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void logsQuery.refetch()}
          >
            Retry logs
          </Button>
        </div>
      )}
      <LogViewer
        key={`${source}:${queryString}`}
        logs={rows}
        showSource
        showTaskName={showTaskName}
        showStepDelta
        taskStartTimes={taskStartTimes}
        defaultRelativeTime={false}
        isLoading={isLoading}
        emptyMessage="No matching events or logs in the loaded history."
        onViewRun={onViewRun}
        onAtTopChange={setAtTop}
        onScrollToBottom={source === 'events' ? undefined : fetchOlderLogs}
      />
      {hasOlderLogs && source !== 'events' && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
          role="status"
        >
          <span>
            Older history is not loaded. Filters apply to the loaded history.
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={logsQuery.isFetchingNextPage}
            onClick={fetchOlderLogs}
          >
            {logsQuery.isFetchingNextPage
              ? 'Loading older history...'
              : 'Load older history'}
          </Button>
        </div>
      )}
      {!isAtTop && (
        <p className="text-xs text-muted-foreground">
          Live log updates paused. Scroll to the top to resume.
        </p>
      )}
    </div>
  );
}
